import { useSetupProgress } from 'src/features/organizations/components'

export type BuildStepStatus = 'queued' | 'running' | 'done' | 'skipped'

export type BuildStepKind = 'roles' | 'signIn' | 'apps' | 'demoData'

export type BuildStep = {
  kind: BuildStepKind
  status: BuildStepStatus
}

export type BuildActivity = {
  percent: number
  steps: BuildStep[]
  settledNewestFirst: BuildStep[]
  /** What the workspace is doing right now, for the live pill. */
  runningKind: BuildStepKind | null
  canOpenWorkspace: boolean
  hasFailedStep: boolean
  isAwaitingSteps: boolean
  demoDataSkipped: boolean
}

/**
 * The four things the screen reports, in the order they happen. Demo data is
 * last because it is the only one the owner chose — and the only one that can be
 * absent, in which case it still holds the bottom row rather than vanishing.
 */
const SEEDED_KINDS: BuildStepKind[] = ['roles', 'signIn', 'apps', 'demoData']

const CLEAN_KINDS: BuildStepKind[] = ['roles', 'signIn', 'apps']

const hasReported = ({ status }: BuildStep): boolean => status === 'done'

/**
 * Turns the provisioning poll into the four categories the panel shows.
 *
 * The pipeline reports its own steps — forty-odd of them, named for the tables
 * they seed (`ATS candidate stages`, `CRM IT nomenclatures`). Listing those told
 * the owner nothing and pushed the panel off the bottom of the screen, so the
 * run is bucketed instead: the percent the backend reports is read against one
 * threshold per category. The number is still the backend's, only the labels are
 * ours.
 *
 * A workspace created from the clean tile was never seeded, so its demo row is
 * greyed out and takes no share of the progress — the other three split it.
 */
export const useBuildActivity = (
  setupJobId: number | undefined,
  demoDataSkipped = false
): BuildActivity => {
  const { jobs, percent, hasError, allDone } = useSetupProgress(setupJobId)

  const kinds = demoDataSkipped ? CLEAN_KINDS : SEEDED_KINDS

  /**
   * Where each category sits on the run's own percentage.
   *
   * Not an even split: roles and sign-in methods are applied the moment the
   * workspace exists, so they are already done when the first poll answers.
   * Apps land at a quarter, and the whole remaining three quarters is demo data
   * — it is the only part that scales with how much there is to seed. Dividing
   * the bar into four equal steps made the first two look like work that had not
   * started yet.
   */
  const threshold = (kind: BuildStepKind): number => {
    if (kind === 'roles' || kind === 'signIn') return 0
    if (kind === 'apps') return demoDataSkipped ? 100 : 25

    return 100
  }

  const steps: BuildStep[] = kinds.map((kind) => {
    const reached = percent >= threshold(kind)

    /**
     * A failure anywhere in the run says nothing about *which* category broke —
     * the pipeline's steps do not map onto these buckets one to one. The failure
     * is reported once, by the run's own status, and the categories simply stop
     * advancing.
     */
    const status: BuildStepStatus = reached
      ? 'done'
      : kind === 'demoData' && percent < threshold('apps')
        ? 'queued'
        : 'running'

    return { kind, status }
  })

  if (demoDataSkipped) {
    steps.push({ kind: 'demoData', status: 'skipped' })
  }

  return {
    percent,
    steps,
    settledNewestFirst: steps.filter(hasReported).reverse(),
    runningKind: steps.find(({ status }) => status === 'running')?.kind ?? null,
    canOpenWorkspace: setupJobId === undefined || allDone || hasError,
    hasFailedStep: hasError,
    isAwaitingSteps: setupJobId !== undefined && jobs.length === 0,
    demoDataSkipped
  }
}
